/**
 * The five voice buttons, live, side by side, on a page you can change the colour
 * of underneath them.
 *
 * WHAT THIS SCREEN IS FOR. Every one of these widgets gets pasted onto a page
 * nobody here has seen — a dentist's WordPress theme, a Shopify storefront, a
 * near-black marketing site with a sticky header. The question that matters is not
 * "does the button work" but "does it still read as a button when it lands
 * somewhere we did not design for", and the only way to answer it is to put it on
 * a surface you can change while looking at it. Hence the background swatches and
 * the per-card preview theme: they are the point of the screen, not decoration.
 *
 * ONE VARIANT AT A TIME, AS TABS, side flag first (product owner 2026-08-23).
 * Five tall cards stacked meant the last widget was several viewports of
 * scrolling away. Each tab's panel is a three-column grid: the sticky test area
 * in the first column, ConfigEditor's card stack spanning the other two (see
 * its header for the stack-not-grid argument). The inactive panels stay MOUNTED
 * (forceMount + hidden): per-variant form state would survive anyway, but a
 * live call's widget unmounting on a tab switch would stop its render loop, and
 * the tab strip marks which hidden tab holds the call instead.
 *
 * ---------------------------------------------------------------------------
 * THE FIXED-POSITION PROBLEM, AND THE ONE LINE THAT SOLVES IT.
 *
 * Four of these five widgets are `position: fixed` — that is what a floating
 * launcher IS — and the flag always is. Previewing against the real viewport
 * would cover this page's own sidebar and header. Scoping them to their stage is
 * therefore not a nicety, it is the difference between a page that works and a
 * page that cannot be used.
 *
 * A TRANSFORMED ANCESTOR IS A CONTAINING BLOCK FOR FIXED POSITIONING. Any
 * `transform` other than `none` makes an element the containing block for its
 * fixed-position descendants, so `position: fixed; bottom: 0; right: 0` inside
 * `PreviewStage` resolves against the STAGE rather than the window. That is the
 * whole mechanism: `transform: translateZ(0)` on the stage, with
 * `contain: layout paint` beside it as belt and braces — `contain` establishes the
 * same containing block and additionally clips, so a widget that positions itself
 * slightly outside is trimmed at the card edge instead of escaping. Verified in
 * the browser with all five cards mounted: each widget sits in its own card, and
 * nothing reaches the page chrome.
 *
 * The cost, stated because it is a real one: a portal that renders to
 * `document.body` is OUTSIDE the stage and would not be contained. None of these
 * five widgets portals. If one ever does, it will escape and this comment is where
 * to start.
 *
 * ---------------------------------------------------------------------------
 * ONE CALL, ROUTED. NOT FIVE.
 *
 * Five `useVoiceControl` instances would be five microphones, five peer
 * connections and five billed sessions, from one page, on every visit. So there is
 * exactly ONE control on this screen and it is handed to whichever card was
 * activated last; the other four get a frozen idle control whose `start` claims
 * the live slot instead of opening anything. Claiming while a call is actually up
 * is refused with a sentence naming the card that has it, rather than silently
 * tearing down a live conversation — a WebRTC offer is not idempotent and
 * end-then-immediately-start is the one sequence most likely to leave a session
 * half-open.
 *
 * ---------------------------------------------------------------------------
 * AND THE OTHER SOURCE: A CALL THAT ISN'T ONE.
 *
 * "Live call" is one of two preview sources; the other fabricates the whole
 * `VoiceControl` (`use-simulated-control.ts`). It exists because a real call is a
 * bad instrument for the question this screen asks. Most of the states a widget
 * has cannot be held still on a real one — `connecting` lasts about a second,
 * `thinking` a fraction of one, and `error` cannot be reached at all without
 * breaking something on purpose — and looking at a colour should not cost a
 * session, an agent, or a bill. The simulator holds any of the eight states for as
 * long as you look at it, feeds the visualisers voice-shaped synthetic audio (or
 * your own microphone, opened by THIS PAGE and never by a widget), and is
 * indistinguishable to the widgets, because a widget only ever sees the interface.
 *
 * Switching source ENDS a live call rather than leaving one running behind a
 * fabricated preview — the same rule, and the same reason, as changing agent.
 *
 * A VIEWER GETS THE WHOLE SCREEN AND NONE OF THE WRITES. Starting a call is a
 * POST and creating an embed is a POST, so both are absent rather than disabled —
 * the rule the users screen and the agent form follow. The config forms stay live,
 * because changing them writes nothing and looking at the widgets is the entire
 * reason a read-only role is shown this page.
 */
import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import {
  AudioLinesIcon,
  BarChart3Icon,
  CircleDotIcon,
  FlagIcon,
  MessagesSquareIcon,
  MicIcon,
  PillIcon,
  MoonIcon,
  MousePointerClickIcon,
  PhoneCallIcon,
  SunIcon,
  WandSparklesIcon,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { ApiError, type AgentConfig } from '@/api'
import { can } from '@/lib/auth'
import {
  embeds as embedsApi,
  inlineSnippetFor,
  snippetFor,
  supportsFloating,
  supportsInline,
  type EmbedRow,
  type EmbedVariant,
} from '@/lib/embeds'
import { useAuth } from '@/components/auth-provider'
import { PageHeader } from '@/components/data-table'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import {
  isBusy,
  type VoiceControl,
  type VoicePhase,
  type VoiceWidgetCommon,
} from '@/components/voice/types'
import { useVoiceControl } from '@/components/voice/use-voice-control'
import { useSimulatedControl, type SimulatedAudio } from '@/components/voice/use-simulated-control'
import {
  ConfigEditor,
  configFor,
  defaultConfig,
  originsClean,
  type PlaygroundConfig,
} from '@/components/voice/config-editor'
import { SnippetBox } from '@/components/voice/snippet-box'
import { FlagButton } from '@/components/voice/flag-button'
import { ChatFlag } from '@/components/voice/chat-flag'
import { ConversationBar } from '@/components/voice/conversation-bar'
import { HoverPill } from '@/components/voice/hover-pill'
import { OrbWidget } from '@/components/voice/orb-widget'
import { PlainButton } from '@/components/voice/plain-button'
import { BarsVisualizer } from '@/components/voice/bars-visualizer'
import { cn } from '@/lib/utils'

/**
 * Written onto the session row, so a call started from here is distinguishable
 * from a real one when somebody reads the sessions list a week later and wonders
 * why there are eleven four-second calls in a row.
 */
const SESSION_LABEL = 'playground/buttons'

/** Inside the stage's own stacking context, so the value only has to beat the
 *  stage's other children. */
const PREVIEW_Z = 5

/** The three surfaces a widget has to survive. Nothing in between is interesting:
 *  what breaks a button is the extremes and the mid grey that is close to both. */
const BACKGROUNDS: { key: string; label: string; css: string }[] = [
  { key: 'white', label: 'White', css: '#ffffff' },
  { key: 'grey', label: 'Mid grey', css: '#8b8b90' },
  { key: 'black', label: 'Near-black', css: '#0b0b0d' },
]

interface VariantMeta {
  variant: EmbedVariant
  title: string
  icon: LucideIcon
  blurb: ReactNode
}

const VARIANTS: VariantMeta[] = [
  {
    variant: 'flag',
    title: 'Side Flag',
    icon: FlagIcon,
    blurb: (
      <>
        A tab welded to the edge of the window, rotated text, always visible. The one variant
        that cannot be placed in flow — it is defined by being on the viewport edge.
      </>
    ),
  },
  {
    variant: 'chat',
    title: 'Chat Flag',
    icon: MessagesSquareIcon,
    blurb: (
      <>
        The same edge tab, opening a chat window instead of a pill: the conversation in
        writing as it happens, and a box to type into for a visitor who cannot or would
        rather not talk. It keeps the transcript on screen after the call ends, which is
        the one thing the plain flag throws away.
      </>
    ),
  },
  {
    variant: 'bar',
    title: 'Conversation Bar',
    icon: AudioLinesIcon,
    blurb: (
      <>
        A rounded-square call button parked in a corner. One click opens it sideways into
        the full control — waveform, mute, and optionally a text box for a visitor with no working
        microphone — and starts the call in the same click. It collapses back to the button
        when the call ends, so nothing sits over the page in between.
      </>
    ),
  },
  {
    variant: 'bars',
    title: 'Bars Visualizer',
    icon: BarChart3Icon,
    blurb: (
      <>
        A wide, cinematic spectrum: mirrored bars, a drifting particle field, and one
        control at the centre. It reacts to both sides of the conversation without
        opening a second microphone. Inline only, and it depends on no stylesheet.
      </>
    ),
  },
  {
    variant: 'orb',
    title: 'Voice Orb',
    icon: CircleDotIcon,
    blurb: (
      <>
        The animated voice object on its own: a compact, phase-aware control for pages
        that do not have room for a waveform or the wide Bars stage.
      </>
    ),
  },
  {
    variant: 'pill',
    title: 'Hover Pill',
    icon: PillIcon,
    blurb: (
      <>
        A white capsule with an orange dot in it. On hover the dot expands into the whole
        button and a microphone slides in behind the call-to-action; on a live call the
        pill is orange, with a stop control and two audio meters. A close port of the
        voice2me button, down to the 100.8&times; scale factor on the dot.
      </>
    ),
  },
  {
    variant: 'button',
    title: 'Plain Button',
    icon: MousePointerClickIcon,
    blurb: (
      <>
        A button. No canvas, no animation frame, no waveform — which makes it the one that
        survives a page with a strict style reset and the one to reach for when in doubt.
      </>
    ),
  },
]

/** How each phase reads as a pill. Colour is never the only signal — the label is
 *  the phase name itself, so a monochrome screenshot still says which. */
const PHASE_PILL: Record<VoicePhase, string> = {
  idle: 'pill-plain',
  connecting: 'border-ev-info-border bg-ev-info-bg text-ev-info',
  listening: 'border-ev-good-border bg-ev-good-bg font-semibold text-ev-good',
  thinking: 'border-ev-warn-border bg-ev-warn-bg font-semibold text-ev-warn',
  speaking: 'border-ev-info-border bg-ev-info-bg font-semibold text-ev-info',
  ended: 'pill-plain',
  error: 'border-ev-bad-border bg-ev-bad-bg font-semibold text-ev-bad',
}

/** The order the simulator's state strip offers, which is the order a call goes
 *  through — `idle` first because that is what a visitor sees, `error` last
 *  because it is the one nobody can reach on purpose during a real call. There is
 *  no `ending` here because there is no `ending` for a widget to render: the
 *  hang-up shows as ended immediately. See `VoicePhase`. */
const SIM_PHASES: readonly VoicePhase[] = [
  'idle',
  'connecting',
  'listening',
  'thinking',
  'speaking',
  'ended',
  'error',
]

const PHASE_MEANS: Record<VoicePhase, string> = {
  idle: 'Nothing has happened yet. No microphone, no session.',
  connecting: 'Opening the microphone and creating the session. Both collapse into one phase because a visitor cannot act on the difference.',
  listening: 'The microphone is open and the turn is the visitor’s.',
  thinking: 'The visitor stopped and the agent has not started. Derived here, not reported by the provider.',
  speaking: 'The agent is talking.',
  ended: 'Finished normally. Shown from the moment the end button is pressed — the teardown that follows is real work, and deliberately not a state of its own.',
  error: 'See the message. Only a blocked microphone is something the visitor can fix.',
}



/** The shared half of a widget's props, pulled off the flat playground config. */
function commonOf(c: PlaygroundConfig): VoiceWidgetCommon {
  return {
    label: c.label,
    endLabel: c.endLabel,
    accent: c.accent,
    onAccent: c.onAccent,
    caption: c.caption,
    theme: c.theme,
  }
}

// Frozen so that handing the same idle control to three cards cannot cause three
// animation-frame loops to restart on every render of this page.
const NO_LEVELS = () => ({ user: 0, assistant: 0 })
const NO_NOTICES: readonly string[] = []
const NO_TRANSCRIPT: VoiceControl['transcript'] = []
const NO_TOOLS: VoiceControl['tools'] = []
const NO_CARDS: VoiceControl['cards'] = []
const NOOP = () => {}

/**
 * A control that is not connected to anything, for the three cards that are not
 * live. Its `start` claims the live slot rather than opening a session, which is
 * what makes "click any card to make it the live one" work without four sessions.
 */
function idleControl(start: () => void): VoiceControl {
  return {
    phase: 'idle',
    error: null,
    notices: NO_NOTICES,
    muted: false,
    startedAtMs: null,
    transcript: NO_TRANSCRIPT,
    tools: NO_TOOLS,
    cards: NO_CARDS,
    levels: NO_LEVELS,
    start,
    stop: NOOP,
    toggleMute: NOOP,
    sendText: NOOP,
  }
}

/** The five embed error codes, as sentences with a next action. Anything else is
 *  the server's own message: a code nobody anticipated is better reported than
 *  paraphrased. */
function messageFor(e: unknown): string {
  if (!(e instanceof ApiError)) return e instanceof Error ? e.message : String(e)
  switch (e.code) {
    case 'read_only':
      return 'Your role can look at this page but not save an embed.'
    case 'bad_field':
      return `The server refused a field: ${e.message}`
    case 'bad_origin':
      return `That origin list was refused: ${e.message}`
    case 'no_such_agent':
      return 'That agent no longer exists. Reload the page and pick another.'
    case 'embed_limit':
      return 'This agent already has as many embeds as it is allowed. Update one of the existing ones rather than adding another.'
    default:
      return e.message
  }
}

export function PlaygroundButtonsPage({
  agents,
}: {
  /**
   * The agent list, already fetched once by the shell. A prop rather than a
   * second fetch, for the reason in App.tsx's header: one poller per endpoint.
   * Null until it lands.
   */
  agents: AgentConfig[] | null
}) {
  const { user } = useAuth()
  const canWrite = can(user, 'write')

  // Derived, not seeded by an effect: `null` here means "the reader has not
  // chosen", and the first agent stands in until they do. An effect that copied
  // the first id into state would be a second source of truth and a StrictMode
  // double-invocation to reason about.
  const [chosen, setChosen] = useState<string | null>(null)
  const agentId = chosen ?? agents?.[0]?.id ?? null
  const agentName = agents?.find((a) => a.id === agentId)?.name ?? agentId ?? ''

  // THE one control on this page. Created unconditionally — hooks cannot be
  // conditional — and never started without an agent, which is why an empty
  // string here is safe rather than a call against no agent.
  const control = useVoiceControl({ agentId: agentId ?? '', label: SESSION_LABEL })
  const [liveVariant, setLiveVariant] = useState<EmbedVariant | null>(null)
  /** The card whose activation was refused because another card holds the call. */
  const [contention, setContention] = useState<EmbedVariant | null>(null)
  const busy = isBusy(control.phase)

  /** Which variant's panel is showing. The side flag first: it is the variant
   *  customers install most and the one the page opens on by decision. */
  const [tab, setTab] = useState<EmbedVariant>('flag')

  /**
   * LIVE CALL, OR THE SIMULATOR.
   *
   * The second one is not a lesser preview, it is the one that answers the
   * question this page asks. A real call shows you `listening` and `speaking` and
   * hurries past everything else: `connecting` lasts a second, `thinking` a
   * fraction of one, and `error` cannot be reached at all without breaking
   * something. The simulator holds any of the eight states still, costs no
   * session, needs no agent, and — because a widget only ever sees a
   * `VoiceControl` — renders exactly what the real thing renders. See
   * use-simulated-control.ts.
   */
  const [preview, setPreview] = useState<'live' | 'simulated'>('live')
  const [simPhase, setSimPhase] = useState<VoicePhase>('idle')
  const [simAudio, setSimAudio] = useState<SimulatedAudio>('synthetic')
  const { control: simControl, micError, micLive } = useSimulatedControl({
    enabled: preview === 'simulated',
    phase: simPhase,
    setPhase: setSimPhase,
    audio: simAudio,
  })

  const [previewDark, setPreviewDark] = useState(false)
  const [background, setBackground] = useState('white')
  const backgroundCss = BACKGROUNDS.find((b) => b.key === background)?.css ?? '#ffffff'

  const [configs, setConfigs] = useState<Record<EmbedVariant, PlaygroundConfig>>(() => ({
    flag: defaultConfig('flag'),
    chat: defaultConfig('chat'),
    bar: defaultConfig('bar'),
    bars: defaultConfig('bars'),
    orb: defaultConfig('orb'),
    button: defaultConfig('button'),
    pill: defaultConfig('pill'),
  }))
  const [snippetMode, setSnippetMode] = useState<Record<EmbedVariant, 'floating' | 'inline'>>({
    flag: 'floating',
    chat: 'floating',
    bar: 'floating',
    // Never read for bars — the mode is forced below — but seeded honestly so a
    // reader of this object is not told something the page does not do.
    bars: 'inline',
    orb: 'inline',
    button: 'floating',
    pill: 'floating',
  })

  const [rows, setRows] = useState<EmbedRow[] | null>(null)
  /**
   * Where snippets point their `src`. The deployment's EMBED_SCRIPT_ORIGIN —
   * the workers.dev host, NOT this panel's own origin: the dev-labs.io zone
   * sits behind a Cloudflare managed challenge that intercepts cross-site
   * subresource requests before our worker answers (no CORS headers on the
   * challenge, so the widget dies silently on a customer's page). Verified
   * against a local host page 2026-08-23. Falls back to this origin only when
   * the deployment never set the var.
   */
  const [scriptOrigin, setScriptOrigin] = useState('')
  const [listError, setListError] = useState<string | null>(null)
  // Bumped after every successful write. A counter, so two consecutive saves are
  // two distinct dependency values.
  const [epoch, setEpoch] = useState(0)
  /** Per-variant verdict of the origins auto-save, for the status line. */
  const [originsSave, setOriginsSave] = useState<
    Record<string, { kind: 'saving' | 'saved' | 'error'; message?: string }>
  >({})

  /**
   * Local edits to an origin list, keyed by VARIANT ALONE: the key is
   * agent-agnostic, so the origin list is a property of the widget type, not of
   * whichever agent the picker happens to show. Absent means "not edited", and
   * the saved record is read instead — which is also how a completed auto-save
   * discards the local copy.
   */
  const [originEdits, setOriginEdits] = useState<Record<string, string[]>>({})

  useEffect(() => {
    const ac = new AbortController()
    embedsApi
      .list(ac.signal)
      .then((res) => {
        if (ac.signal.aborted) return
        setRows(res.rows)
        setScriptOrigin(res.scriptOrigin)
        setListError(null)
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        setListError(messageFor(e))
      })
    return () => ac.abort()
  }, [epoch])

  /** PER VARIANT, agent ignored: one key per widget type. Rows from before the
   *  agent-agnostic change were per agent+variant; the oldest live one wins, so
   *  the key already on customers' pages keeps being the one this page shows. */
  const embedFor = (v: EmbedVariant): EmbedRow | null =>
    rows?.find((r) => r.variant === v && r.revokedAt === null) ?? null

  /**
   * THE KEYS MINT THEMSELVES. A snippet is a pure function of the settings on
   * screen plus a key, so a missing key is plumbing the reader should never see
   * — the page creates one per variant on first load instead of offering a
   * button. The agent on the record is only the legacy fallback column; routing
   * lives in the tag.
   */
  const ensuredKeys = useRef(false)
  useEffect(() => {
    if (!canWrite || rows === null || agentId === null || ensuredKeys.current) return
    const missing = VARIANTS.map((m) => m.variant).filter(
      (v) => !rows.some((r) => r.variant === v && r.revokedAt === null),
    )
    ensuredKeys.current = true
    if (missing.length === 0) return
    void (async () => {
      for (const v of missing) {
        try {
          await embedsApi.create(
            {
              agentId,
              name: VARIANTS.find((m) => m.variant === v)?.title ?? v,
              variant: v,
              allowedOrigins: [],
            },
            AbortSignal.timeout(20_000),
          )
        } catch (e) {
          setListError(messageFor(e))
        }
      }
      setEpoch((n) => n + 1)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canWrite, rows, agentId])

  const originsFor = (v: EmbedVariant): string[] =>
    originEdits[v] ?? embedFor(v)?.allowedOrigins ?? []

  /**
   * AUTO-SAVE, DEBOUNCED. Edits land in `originEdits` immediately; 800 ms after
   * the last keystroke the list is persisted IF every row is valid (the
   * row-level errors already say why an invalid one is waiting) and actually
   * different from the record. The PATCH response replaces the row locally, so
   * nothing flashes back to the old list, and the local edit is dropped only if
   * the reader has not typed since — a keystroke during the flight wins.
   */
  const originEditsRef = useRef(originEdits)
  originEditsRef.current = originEdits
  const rowsRef = useRef(rows)
  rowsRef.current = rows
  const saveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})

  const persistOrigins = (v: EmbedVariant) => {
    const embed = rowsRef.current?.find((r) => r.variant === v && r.revokedAt === null) ?? null
    const edited = originEditsRef.current[v]
    if (!embed || edited === undefined) return
    if (!originsClean(edited) || edited.some((o) => o.trim() === '')) return
    const next = edited.map((o) => o.trim())
    if (JSON.stringify(next) === JSON.stringify(embed.allowedOrigins)) {
      setOriginsSave((prev) => ({ ...prev, [v]: { kind: 'saved' } }))
      return
    }
    setOriginsSave((prev) => ({ ...prev, [v]: { kind: 'saving' } }))
    // NO AbortController TIED TO THIS COMPONENT — a write must survive
    // navigation (the AgentEdit rule); a timeout bounds it instead.
    embedsApi
      .patch(embed.id, { allowedOrigins: next }, AbortSignal.timeout(20_000))
      .then((row) => {
        setRows((prev) => prev?.map((r) => (r.id === row.id ? row : r)) ?? prev)
        setOriginEdits((prev) => {
          if (JSON.stringify(prev[v]) !== JSON.stringify(edited)) return prev
          const nextEdits = { ...prev }
          delete nextEdits[v]
          return nextEdits
        })
        setOriginsSave((prev) => ({ ...prev, [v]: { kind: 'saved' } }))
      })
      .catch((e: unknown) => {
        setOriginsSave((prev) => ({ ...prev, [v]: { kind: 'error', message: messageFor(e) } }))
      })
  }

  const setOriginsFor = (v: EmbedVariant, update: (prev: string[]) => string[]) => {
    // Functional and reconstructing: a variant with a saved embed and no local
    // edit has no entry at all, so the saved origins are the base of the edit.
    setOriginEdits((prev) => {
      const current = prev[v] ?? embedFor(v)?.allowedOrigins ?? []
      return { ...prev, [v]: update(current) }
    })
    clearTimeout(saveTimers.current[v])
    saveTimers.current[v] = setTimeout(() => persistOrigins(v), 800)
  }

  useEffect(() => {
    const timers = saveTimers.current
    return () => Object.values(timers).forEach(clearTimeout)
  }, [])

  /**
   * Hand the one call to this card.
   *
   * Refused while a call is up, rather than moving it. Ending one session and
   * opening another in the same tick is the sequence most likely to leave a
   * half-open session behind, and "end the call in the other card first" is a
   * sentence a reader can act on.
   */
  const claim = (v: EmbedVariant) => {
    if (!canWrite || agentId === null) return
    if (liveVariant !== null && liveVariant !== v && busy) {
      setContention(v)
      return
    }
    setContention(null)
    setLiveVariant(v)
    control.start()
  }

  const controlFor = (v: EmbedVariant): VoiceControl =>
    preview === 'simulated'
      // One simulated control for all five cards, deliberately: they are five views
      // of one imaginary call, so switching tabs continues the same state rather
      // than resetting it.
      ? simControl
      : liveVariant === v
        ? control
        : idleControl(() => claim(v))

  /**
   * Switching modes ENDS A LIVE CALL, and does not ask first.
   *
   * The alternative is a real conversation running behind previews that are showing
   * a fabricated one — the screen lying about what is connected, which is the same
   * reason changing agent stops the call in `onAgent` below.
   */
  const onPreviewMode = (next: 'live' | 'simulated') => {
    if (next === preview) return
    if (busy) control.stop()
    setLiveVariant(null)
    setContention(null)
    setSimPhase('idle')
    if (next === 'live') setSimAudio('synthetic')
    setPreview(next)
  }

  /**
   * Switching agent ends whatever is running.
   *
   * Done in the handler and not in an effect: a live call belongs to the agent it
   * was created against, and leaving it up under a different name in the dropdown
   * would be the screen lying about what is connected.
   */
  const onAgent = (id: string) => {
    if (busy) control.stop()
    setLiveVariant(null)
    setContention(null)
    setChosen(id)
  }


  return (
    <div className="min-w-0">
      <PageHeader
        title="Voice Widgets"
        subtitle={
          <>
            The five widgets a customer can drop on their own page — one per tab, live against a
            real agent or driven by the simulator, which fakes a call so every state holds
            still and nothing is dialled. Change the colour of the page underneath them,
            because that is the variable nobody controls: a floating launcher that reads
            perfectly on this panel can vanish on a near-black marketing site.
          </>
        }
      />

      {/* The tools that apply to every card. STICKY, because the cards are tall and
          the agent you are calling is the first thing you want to change after
          scrolling to the fourth one. */}
      <div className="sticky top-0 z-30 -mx-4 mb-5 flex flex-wrap items-end gap-4 border-b bg-background/95 px-4 py-3 backdrop-blur md:-mx-6 md:px-6">
        <div className="field w-64 gap-1.5">
          <Label htmlFor="playgroundAgent">Agent</Label>
          {agents === null ? (
            <p className="loading p-0">Loading agents…</p>
          ) : agentId === null ? (
            <p className="text-sm text-muted-foreground">None configured</p>
          ) : (
            <Select value={agentId} onValueChange={onAgent}>
              <SelectTrigger id="playgroundAgent" size="sm" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {agents.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="field gap-1.5">
          <Label>Preview theme</Label>
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            spacing={0}
            value={previewDark ? 'dark' : 'light'}
            onValueChange={(v) => {
              if (v === 'light' || v === 'dark') setPreviewDark(v === 'dark')
            }}
          >
            <ToggleGroupItem value="light">
              <SunIcon /> Light
            </ToggleGroupItem>
            <ToggleGroupItem value="dark">
              <MoonIcon /> Dark
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div className="field gap-1.5">
          <Label>Page background</Label>
          <div className="flex items-center gap-1.5">
            {BACKGROUNDS.map((b) => (
              <button
                key={b.key}
                type="button"
                aria-label={b.label}
                aria-pressed={background === b.key}
                title={b.label}
                onClick={() => setBackground(b.key)}
                className={cn(
                  'size-7 rounded-md border transition-all',
                  background === b.key
                    ? 'border-ring ring-3 ring-ring/50'
                    : 'border-input hover:border-ring',
                )}
                style={{ background: b.css }}
              />
            ))}
          </div>
        </div>

        <div className="field gap-1.5">
          <Label>Preview source</Label>
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            spacing={0}
            value={preview}
            onValueChange={(v) => {
              if (v === 'live' || v === 'simulated') onPreviewMode(v)
            }}
          >
            <ToggleGroupItem value="live">
              <PhoneCallIcon /> Live call
            </ToggleGroupItem>
            <ToggleGroupItem value="simulated">
              <WandSparklesIcon /> Simulator
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        {preview === 'live' ? (
          <p className="max-w-md text-xs text-muted-foreground">
            The theme switch changes the page the widget sits on, not the widget: each card
            has its own <span className="mono">theme</span> field for that, and{' '}
            <span className="mono">auto</span> is where the two meet. The{' '}
            <span className="font-medium">simulator</span> drives every widget through a
            fake call — no agent, no session, no bill — and holds any state still.
          </p>
        ) : (
          <>
            <div className="field gap-1.5">
              <Label>Simulated state</Label>
              <ToggleGroup
                type="single"
                variant="outline"
                size="sm"
                spacing={0}
                value={simPhase}
                onValueChange={(v) => {
                  const found = SIM_PHASES.find((p) => p === v)
                  if (found) setSimPhase(found)
                }}
              >
                {SIM_PHASES.map((p) => (
                  <ToggleGroupItem key={p} value={p} title={PHASE_MEANS[p]}>
                    {p}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>

            <div className="field gap-1.5">
              <Label>Simulated audio</Label>
              <ToggleGroup
                type="single"
                variant="outline"
                size="sm"
                spacing={0}
                value={simAudio}
                onValueChange={(v) => {
                  if (v === 'synthetic' || v === 'microphone') setSimAudio(v)
                }}
              >
                <ToggleGroupItem value="synthetic">Synthetic</ToggleGroupItem>
                <ToggleGroupItem value="microphone">
                  <MicIcon /> My microphone
                </ToggleGroupItem>
              </ToggleGroup>
            </div>

            <p className="max-w-md text-xs text-muted-foreground">
              Nothing is dialled and no agent is involved: the widgets are driven by a
              fabricated call, so every state holds still for as long as you look at it.
              The waveforms are voice-shaped synthetic audio unless you lend the page your
              microphone — which the <em>page</em> opens, never the widget.
              {micLive && <span className="text-ev-info"> Microphone live.</span>}
            </p>
          </>
        )}
      </div>

      {preview === 'simulated' && micError && (
        <p className="mb-5 max-w-3xl text-sm text-ev-warn">{micError}</p>
      )}

      {!canWrite && (
        <p className="mb-5 max-w-3xl text-sm text-muted-foreground">
          Read-only: starting a call and creating an embed are both writes, and the{' '}
          <span className="mono">{user?.role}</span> role cannot do either — so there is
          nothing to save and no call to start. The{' '}
          <span className="font-medium">simulator</span> above is not a write and works
          fully, so the widgets can still be seen in every state; so do the config forms,
          which only change what you are looking at.
        </p>
      )}

      {listError && (
        <p className="err mb-5">
          The embed list could not be read ({listError}). The previews below still work; the
          snippets do not, because a snippet needs a saved key.
        </p>
      )}

      {agents !== null && agentId === null && (
        <p className="err mb-5">
          No agents are configured, so there is nothing to call. The previews below render
          their idle state and will not start.
        </p>
      )}

      <Tabs
        value={tab}
        onValueChange={(v) => {
          if (v === 'flag' || v === 'chat' || v === 'bar' || v === 'bars' || v === 'orb' || v === 'button' || v === 'pill') setTab(v)
        }}
      >
        <TabsList className="mb-4">
          {VARIANTS.map((m) => (
            <TabsTrigger key={m.variant} value={m.variant}>
              <m.icon aria-hidden />
              {m.title}
              {/* The one thing a hidden tab must not hide: it holds the live
                  call. The strip says so wherever the reader is. */}
              {liveVariant === m.variant && busy && <span className="live-dot" aria-hidden />}
            </TabsTrigger>
          ))}
        </TabsList>

        {VARIANTS.map((meta) => {
        const v = meta.variant
        const cfg = configs[v]
        const cardControl = controlFor(v)
        const live = liveVariant === v
        const embed = embedFor(v)
        // The predicates, not variant names: three of the five have only one form
        // each — a flag is always an edge tab, the call button always floats in a
        // corner, and Bars is always in the flow — and a fourth is a matter of time.
        const mode = !supportsInline(v) ? 'floating' : !supportsFloating(v) ? 'inline' : snippetMode[v]
        // Recomputed every render from the CURRENT settings, so the snippet is
        // always the widget on screen — "generate" is just copying it. The
        // appearance is inlined as data-attributes; only the key comes from the
        // saved record.
        const code =
          embed === null
            ? null
            : mode === 'inline'
              ? inlineSnippetFor(embed, scriptOrigin || location.origin, configFor(v, cfg), agentId ?? embed.agentId)
              : snippetFor(embed, scriptOrigin || location.origin, configFor(v, cfg), agentId ?? embed.agentId)

        return (
          // forceMount, hidden when inactive — see the file header: a live call's
          // widget must keep rendering while the reader looks at another tab.
          <TabsContent key={v} value={v} forceMount className="data-[state=inactive]:hidden">
          {/* `live && busy`, not `live` alone: `liveVariant` remembers which card
              last CLAIMED the call slot and never resets, so on its own it kept
              the ring and the pill up next to an "ended" phase — a call nobody
              was on, marked as held. The claim marker only means something while
              the call is actually running; the tab-strip dot above already used
              the same gate. */}
          <section className={cn('panel', live && busy && 'ring-2 ring-ev-info-border')}>
            <h2>
              <meta.icon className="size-3.5" aria-hidden />
              {meta.title}
              <span className="note mono">{v}</span>
              <span className="ml-auto flex items-center gap-2">
                {live && busy && (
                  <span className="pill border-ev-info-border bg-ev-info-bg font-semibold text-ev-info">
                    this tab holds the call
                  </span>
                )}
                <span className={cn('pill', PHASE_PILL[cardControl.phase])} title={PHASE_MEANS[cardControl.phase]}>
                  {cardControl.phase}
                </span>
              </span>
            </h2>

            {/*
              Test area in the first of three columns, the editor's card stack in
              the other two (its root carries the col-span — see config-editor's
              header for why it is a deliberate stack, not an auto-flowed grid).
              The preview is sticky so the widget stays in view while the reader
              scrolls the publish card; the offset clears the sticky toolbar.
            */}
            <div className="body grid gap-4 lg:grid-cols-3">
              <div className="flex min-w-0 flex-col gap-3 lg:sticky lg:top-28 lg:self-start">
                <p className="text-sm text-muted-foreground">{meta.blurb}</p>

                <PreviewStage dark={previewDark} background={backgroundCss}>
                  {v === 'flag' && (
                    <FlagButton
                      control={cardControl}
                      side={cfg.side}
                      offsetY={cfg.offsetY}
                      width={cfg.width}
                      height={cfg.height}
                      zIndex={PREVIEW_Z}
                      {...commonOf(cfg)}
                    />
                  )}
                  {v === 'chat' && (
                    // Spelled out rather than `commonOf(cfg)`: this variant reads no
                    // `theme` and does read a `title`, and the preview must pass
                    // exactly what the snippet will.
                    <ChatFlag
                      control={cardControl}
                      side={cfg.side}
                      offsetY={cfg.offsetY}
                      width={cfg.width}
                      height={cfg.height}
                      panelWidth={cfg.chatWidth}
                      panelHeight={cfg.chatHeight}
                      title={cfg.chatTitle}
                      surface={cfg.chatSurface}
                      onSurface={cfg.chatOnSurface}
                      surfaceBorder={cfg.chatSurfaceBorder}
                      label={cfg.label}
                      endLabel={cfg.endLabel}
                      accent={cfg.accent}
                      onAccent={cfg.onAccent}
                      caption={cfg.caption}
                      zIndex={PREVIEW_Z}
                    />
                  )}
                  {v === 'bar' && (
                    // NOT `commonOf(cfg)`, which is the one place in this file that
                    // spells its props out. This variant reads no `caption` and no
                    // `theme` — see conversation-bar.tsx — and the preview must pass
                    // exactly what the snippet will, or the card stops being a
                    // preview of the thing being published.
                    <ConversationBar
                      control={cardControl}
                      label={cfg.label}
                      endLabel={cfg.endLabel}
                      accent={cfg.accent}
                      onAccent={cfg.onAccent}
                      placement={cfg.placement}
                      size={cfg.barSize}
                      surface={cfg.barSurface}
                      onSurface={cfg.barOnSurface}
                      surfaceBorder={cfg.barSurfaceBorder}
                      allowText={cfg.allowText}
                      allowMute={cfg.allowMute}
                      zIndex={PREVIEW_Z}
                    />
                  )}
                  {v === 'bars' && (
                    // No placement and no z-index: bars is inline-only (see
                    // supportsFloating), so the preview and the install are the
                    // same object — the stage simply sits where its container is.
                    <BarsVisualizer
                      control={cardControl}
                      barCount={cfg.barCount}
                      particles={cfg.barsParticles}
                      glow={cfg.barsGlow}
                      width={cfg.barsWidth}
                      height={cfg.barsHeight}
                      buttonSize={cfg.barsButtonSize}
                      captionGap={cfg.barsCaptionGap}
                      radius={cfg.barsRadius}
                      background={cfg.barsBackground}
                      buttonColor={cfg.barsButtonColor}
                      iconColor={cfg.barsIconColor}
                      captionColor={cfg.barsCaptionColor}
                      allowMute={cfg.allowMute}
                      {...commonOf(cfg)}
                    />
                  )}
                  {v === 'orb' && <OrbPreview control={cardControl} config={cfg} />}
                  {v === 'pill' && (
                    // Spelled out, not `commonOf(cfg)`: this variant reads no theme and no
                    // endLabel, and does read a hoverLabel the others do not have.
                    <HoverPill
                      control={cardControl}
                      placement={cfg.placement}
                      hoverLabel={cfg.pillHoverLabel}
                      width={cfg.pillWidth}
                      height={cfg.pillHeight}
                      surface={cfg.pillSurface}
                      onSurface={cfg.pillOnSurface}
                      border={cfg.pillBorder}
    sound={cfg.pillSound}
    allowMute={cfg.allowMute}
    meterBars={cfg.pillMeterBars}
                      label={cfg.label}
                      accent={cfg.accent}
                      onAccent={cfg.onAccent}
                      caption={cfg.caption}
                      zIndex={PREVIEW_Z}
                    />
                  )}
                  {v === 'button' && (
                    <PlainButton
                      control={cardControl}
                      size={cfg.buttonSize}
                      placement={cfg.placement}
                      showTimer={cfg.showTimer}
                      zIndex={PREVIEW_Z}
                      {...commonOf(cfg)}
                    />
                  )}
                </PreviewStage>

                {/* Gated on `busy`, not just on the refusal having happened: once
                    the other card's call ends this sentence stops being true, and a
                    stale instruction to go and end a finished call is worse than
                    no instruction. It clears itself rather than needing a reset. */}
                {contention === v && liveVariant !== null && busy && (
                  <p className="text-sm text-ev-warn">
                    A call is running in the{' '}
                    <strong className="font-semibold">
                      {VARIANTS.find((m) => m.variant === liveVariant)?.title}
                    </strong>{' '}
                    card. End it there first — there is one microphone and one session on this
                    page, deliberately.
                  </p>
                )}

                {cardControl.error && <p className="err text-sm">{cardControl.error}</p>}

                {cardControl.notices.length > 0 && (
                  <ul className="flex flex-col gap-1.5 rounded-md border border-ev-warn-border bg-ev-warn-bg px-3 py-2 text-sm text-ev-warn">
                    {cardControl.notices.map((n) => (
                      <li key={n}>{n}</li>
                    ))}
                  </ul>
                )}

                {!supportsInline(v) ? (
                  <p className="text-xs text-muted-foreground">
                    {v === 'flag' || v === 'chat' ? (
                      <>
                        Always floating. A flag is defined by being on the edge of the
                        window, so there is no in-flow form of it and no inline snippet —
                        the side choice is the only placement it has.
                      </>
                    ) : (
                      <>
                        Always floating. This one is a call button parked in a corner of the
                        window, so &ldquo;in this div&rdquo; is not a placement it has — the
                        corner is, and it also decides which way the bar opens.
                      </>
                    )}
                  </p>
                ) : v === 'bars' ? (
                  <p className="text-xs text-muted-foreground">
                    Always inline. Bars is a stage rather than a launcher: pinned to a corner
                    it covered a third of a phone screen with no way to collapse it, so it has
                    no floating form and no placement setting. The snippet below carries a{' '}
                    <span className="mono">&lt;div&gt;</span> to put it in — move that div and
                    the widget moves with it.
                  </p>
                ) : (
                  embed !== null && (
                    <div className="field gap-1.5">
                      <Label>Snippet form</Label>
                      <ToggleGroup
                        type="single"
                        variant="outline"
                        size="sm"
                        spacing={0}
                        value={mode}
                        onValueChange={(next) => {
                          if (next === 'floating' || next === 'inline') {
                            setSnippetMode((prev) => ({ ...prev, [v]: next }))
                          }
                        }}
                      >
                        <ToggleGroupItem value="floating">Floating</ToggleGroupItem>
                        <ToggleGroupItem value="inline">Inline</ToggleGroupItem>
                      </ToggleGroup>
                    </div>
                  )
                )}

                {embed === null ? (
                  <p className="text-xs text-muted-foreground">
                    {canWrite
                      ? 'Minting this variant’s key…'
                      : 'No key exists for this variant yet, and creating one is a write.'}
                  </p>
                ) : (
                  <>
                    <SnippetBox
                      code={code ?? snippetFor(embed, scriptOrigin || location.origin, configFor(v, cfg), agentId ?? embed.agentId)}
                      caption={mode === 'inline' ? 'inline' : 'floating'}
                    />
                    <p className="text-xs text-muted-foreground">
                      The snippet tracks the settings live — copy it when the preview looks
                      right. Every <span className="mono">data-</span> attribute is the
                      customer&rsquo;s to hand-edit on their own page; the key is the only part
                      that has to come from here. <RuntimeGuide />
                    </p>
                  </>
                )}
              </div>

              <ConfigEditor
                variant={v}
                value={cfg}
                // Functional all the way down, so two edits in one React batch
                // both land. See the note on `onChange` in config-editor.tsx.
                onChange={(update) => setConfigs((prev) => ({ ...prev, [v]: update(prev[v]) }))}
                canWrite={canWrite}
                origins={originsFor(v)}
                onOriginsChange={(update) => setOriginsFor(v, update)}
                originsSave={originsSave[v] ?? null}
              />
            </div>
          </section>
          </TabsContent>
        )
        })}
      </Tabs>
    </div>
  )
}

/**
 * The surface a preview lives on, and the containing block that keeps it there.
 *
 * `transform` plus `contain` is the whole mechanism — see the file header. The
 * `dark` class is put on the STAGE rather than on `<html>` so that a card can be
 * previewed dark while the panel around it stays light: Tailwind's dark variant is
 * `&:is(.dark *)`, which matches descendants, so everything inside picks up the
 * dark tokens and nothing outside does.
 */
function PreviewStage({
  dark,
  background,
  children,
}: {
  dark: boolean
  background: string
  children: ReactNode
}) {
  return (
    <div
      className={cn('relative overflow-hidden rounded-md border', dark && 'dark')}
      style={{
        background,
        minHeight: '18rem',
        transform: 'translateZ(0)',
        contain: 'layout paint',
      }}
    >
      {/* Centres the inline placements. A fixed-position widget is out of flow and
          ignores this, which is the point. */}
      <div className="flex min-h-[18rem] items-center justify-center p-4">{children}</div>
    </div>
  )
}

/**
 * The orb card previews `OrbWidget` — THE SAME COMPONENT THE EMBED MOUNTS.
 *
 * It used to render `OrbCanvas` directly, wrapped in a button and a status pill
 * written here. That was a real defect rather than a style choice: `OrbCanvas` is the
 * underlying renderer shared by several controls, and the thing a customer actually
 * installs for `variant: 'orb'` is `OrbWidget`. So the one card whose entire purpose
 * is "see what this looks like before you install it" was previewing something
 * nobody receives, and any divergence between the two would have been invisible here
 * and visible on a customer's website.
 *
 * The only thing this wrapper still adds is the caption pill's readability: the
 * playground lets you put the widget on white, near-black or grey, and the widget's
 * own colours are the reader's to break. That is page chrome, so it stays on the page.
 */
function OrbPreview({
  control,
  config,
}: {
  control: VoiceControl
  config: PlaygroundConfig
}) {
  return (
    <OrbWidget
      control={control}
      size={config.orbSize}
      orbTheme={config.orbTheme}
      placement="inline"
      {...(config.label === undefined ? {} : { label: config.label })}
      {...(config.endLabel === undefined ? {} : { endLabel: config.endLabel })}
      {...(config.caption === undefined ? {} : { caption: config.caption })}
      {...(config.colors === undefined
        ? {}
        : { accent: config.colors.from, onAccent: config.colors.to })}
    />
  )
}

/**
 * The runtime-timing rules, behind a tiny trigger instead of inline — the
 * snippet card is busy enough (product feedback, 2026-08-23). The rules mirror
 * embed/main.tsx: the tag's dataset is captured ONCE when the script evaluates,
 * except `data-client-payload`, which is re-read at every click by design.
 */
function RuntimeGuide() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="ml-1 h-6 cursor-pointer px-2 text-xs font-normal"
        >
          Changing at runtime…
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Changing values at runtime</DialogTitle>
          <DialogDescription>
            What the widget reads once, and what it re-reads on every click.
          </DialogDescription>
        </DialogHeader>
        <div className="flex min-w-0 flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            Appearance attributes and <span className="mono">data-agent-id</span> are read{' '}
            <span className="font-medium text-foreground">once, when the script loads</span> — set
            them in the HTML (or before injecting the tag), and reload to change them.{' '}
            <span className="mono">data-client-payload</span> is the dynamic one: it is{' '}
            <span className="font-medium text-foreground">re-read at the moment the visitor clicks</span>,
            so the page can set or update it at any time — including after the widget is already
            showing.
          </p>
          <SnippetBox
            code={[
              '<script>',
              '  // run whenever your app learns something about the visitor —',
              '  // before or after the widget loads, it counts at the next click',
              '  document.querySelector(\'script[data-key^="pk_"]\')',
              '    .setAttribute(\'data-client-payload\', JSON.stringify({',
              '      userID: currentUser.id,',
              '      plan: currentUser.plan,',
              '    }))',
              '</' + 'script>',
            ].join('\n')}
            caption="dynamic client data"
          />
          <p className="text-xs text-muted-foreground">
            Values must be strings, numbers or booleans — nested objects are dropped with a
            console warning. Whatever the payload holds at the click is what your init webhook
            and tools receive for that session.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
